import PropTypes from 'prop-types';
import React from 'react';
import DateFormats from '../model/DateFormats';
import Genders from '../model/Genders';
import Statuses from '../model/Statuses';
import StatusWithDraft from '../model/StatusWithDraft';
import TimeFormats from '../model/TimeFormats';
import TimeZones from '../model/TimeZones';
import Weekdays from '../model/Weekdays';
import translatePropTypes from './translatePropTypes';
import { iconsList } from './data/hooks/useIconsList';
export const entityFormWrapTypes = {
    editing: PropTypes.bool,
    ...translatePropTypes,
};
export const entityFormPropTypes = {
    ...entityFormWrapTypes,
};
const featuredPermissionPropTypes = {
    enabledPermission: PropTypes.func.isRequired,
};
// DEVNOTE: Input types from MDN:
// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input
const inputTypes = [
    'button',
    'checkbox',
    'color',
    'date',
    'datetime-local',
    'email',
    'file',
    'hidden',
    'image',
    'month',
    'number',
    'password',
    'radio',
    'range',
    'reset',
    'search',
    'submit',
    'tel',
    'text',
    'time',
    'url',
    'week',
    'datetime',
];
const type = PropTypes.oneOf(inputTypes);
const edIcons = [
    'ed-asset-register',
    'ed-groups',
    'ed-note',
    'ed-note-unused',
    'ed-program-intake',
    'ed-staff-learning-space',
    'ed-student-learning-space',
    'ed-timetable',
    'ed-system',
    'ed-facility-management',
    'ed-remove',
];
const pgTypesIcons = [
    'pgt-teaching',
    'pgt-practice',
    'pgt-external-exam',
    'pgt-assignment',
    'pgt-theory',
    'pgt-team-work',
    'pgt-service-activity',
    'pgt-role-play',
    'pgt-pages',
    'pgt-online-survey',
    'pgt-lecture',
    'pgt-multimedia',
    'pgt-lab',
    'pgt-game',
    'pgt-exercise',
    'pgt-exam',
    'pgt-essay',
    'pgt-discussion',
    'pgt-coloring',
    'pgt-debate',
    'pgt-clinical',
    'pgt-class',
    'pgt-book',
    'pgt-art-activity',
    'pgt-assessment',
];
const allIcons = [...edIcons, ...iconsList, ...pgTypesIcons];
const icon = PropTypes.oneOf([...allIcons, PropTypes.node]);
const selectValue = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);
const element = PropTypes.oneOfType([
    PropTypes.element,
    PropTypes.func,
    PropTypes.array,
    PropTypes.shape({ render: PropTypes.func.isRequired }),
    PropTypes.node,
    PropTypes.symbol,
    PropTypes.object,
]);
const routerMatch = PropTypes.shape({ url: PropTypes.string.isRequired });
const routerLocation = PropTypes.shape({
    pathname: PropTypes.string.isRequired,
});
const module = PropTypes.shape({
    title: PropTypes.string.isRequired,
    icon,
    route: PropTypes.string.isRequired,
    component: element,
    exactRoute: PropTypes.bool,
    hasBottomDivider: PropTypes.bool,
    topTitle: PropTypes.string,
});
const moduleGroup = PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    modules: PropTypes.arrayOf(module).isRequired,
});
const modules = PropTypes.shape({
    groups: PropTypes.arrayOf(moduleGroup).isRequired,
    setupGroups: PropTypes.arrayOf(moduleGroup).isRequired,
    profileGroups: PropTypes.arrayOf(moduleGroup).isRequired,
    all: PropTypes.arrayOf(module).isRequired,
    byId: PropTypes.object,
});
const status = PropTypes.oneOf(Statuses.Basic.map(s => s.value));
export const statuses = Statuses.Basic.map(s => s.value);
export const statusesWithDraft = StatusWithDraft.Basic.map(s => s.value);
const role = PropTypes.shape({
    id: PropTypes.number,
    _tempId: PropTypes.string,
    name: PropTypes.string.isRequired,
    roleGroupId: PropTypes.number.isRequired,
    securityProfileId: PropTypes.number,
    status: status.isRequired,
    permissions: PropTypes.arrayOf(PropTypes.string),
    myDriveQuota: PropTypes.number,
});
const roleGroup = PropTypes.shape({
    id: PropTypes.number,
    _tempId: PropTypes.string,
    name: PropTypes.string.isRequired,
    personEntityTypeId: PropTypes.number.isRequired,
    status: status.isRequired,
    parentId: PropTypes.number,
});
const personEntityDefaultType = PropTypes.shape({
    id: PropTypes.number,
    defaultSecurityProfileId: PropTypes.number,
    __typename: PropTypes.string,
    name: PropTypes.string.isRequired,
    isCore: PropTypes.bool,
});
const roleHierarchy = PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    status,
    allBusinessEntities: PropTypes.bool,
    roleAssignments: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        parentId: PropTypes.number,
    })),
    businessEntities: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
    })),
});
const communicationHierarchy = PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    status: status.isRequired,
    category: PropTypes.string.isRequired,
    categoriesAmount: PropTypes.number.isRequired,
    campuses: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
    })),
});
const gender = PropTypes.oneOf(Genders.Basic.map(g => g.value));
const genders = Genders.Basic.map(g => g.value);
const timeFormats = PropTypes.oneOf(TimeFormats.Basic.map(g => g.value));
const weekday = PropTypes.oneOf(Weekdays.Basic.map(g => g.value));
const timeZones = PropTypes.oneOf(TimeZones.Basic.map(g => g.value));
const dateFormats = PropTypes.oneOf(DateFormats.Basic.map(g => g.value));
const organisationType = PropTypes.oneOf([
    'LEVEL',
    'BUSINESS_ENTITY',
    'CAMPUS',
]);
const organisation = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired,
    parentId: PropTypes.number,
    organisationType,
});
const qualification = PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    parentId: PropTypes.number,
    code: PropTypes.string.isRequired,
    itemType: PropTypes.oneOf(['ITEM', 'BRANCH']).isRequired,
});
export const routePropTypes = {
    match: routerMatch.isRequired,
    location: routerLocation.isRequired,
    history: PropTypes.shape({
        push: PropTypes.func.isRequired,
    }).isRequired,
};
const elementsOfType = Component => (props, propName, componentName) => {
    const prop = props[propName];
    let error = null;
    React.Children.forEach(prop, child => {
        if (child && child.type !== Component) {
            const name = Component.name;
            error = new Error(`Only ${name} items are allowed as ${propName} of ${componentName}`);
        }
    });
    return error;
};
export const personEntityType = PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
});
const yearCalendarType = PropTypes.oneOf(['ACADEMIC', 'FINANCIAL']);
const yearCalendar = PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    cycleTypeId: PropTypes.number,
    yearCalendarType: yearCalendarType.isRequired,
    status: status.isRequired,
});
const formErrorMessage = PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
]);
const socialAccountType = PropTypes.oneOf(['GOOGLE', 'MICROSOFT']);
const socialAccount = PropTypes.shape({
    type: socialAccountType.isRequired,
    accountIdentifier: PropTypes.string.isRequired,
    linkedAt: PropTypes.string.isRequired,
});
const passwordStrength = PropTypes.shape({
    minPasswordLength: PropTypes.number.isRequired,
    minUppercaseSymbolsCount: PropTypes.number.isRequired,
    minLowercaseSymbolsCount: PropTypes.number.isRequired,
    minDigitsCount: PropTypes.number.isRequired,
    minSpecialSymbolsCount: PropTypes.number.isRequired,
});
const personPasswordInfo = PropTypes.shape({
    passwordExpiresAt: PropTypes.string.isRequired,
    passwordStrength: passwordStrength.isRequired,
});
const appConfig = PropTypes.shape({
    googleClientId: PropTypes.string,
    microsoftClientId: PropTypes.string,
    turnstileSiteKey: PropTypes.string,
    apiUrl: PropTypes.string.isRequired,
    fileStorageApiUrl: PropTypes.string.isRequired,
});
const sizeUnit = PropTypes.oneOf(['Gb', 'Mb', 'Kb', 'b', 'auto']);
const fileAttachment = PropTypes.shape({
    fileId: PropTypes.string,
    uploadToken: PropTypes.string,
    description: PropTypes.string,
    error: PropTypes.object,
    fileName: PropTypes.string.isRequired,
    mimeType: PropTypes.string.isRequired,
    url: PropTypes.string,
});
const fileUploadTab = PropTypes.oneOf(['Files', 'Images', 'Photos']);
const simpleTableConfig = PropTypes.shape({
    columns: PropTypes.array.isRequired,
    getId: PropTypes.func,
});
const simpleModalConfig = PropTypes.shape({
    entityName: PropTypes.string.isRequired,
    validate: PropTypes.func,
    columns: PropTypes.number,
    component: element.isRequired,
    defaultEntity: PropTypes.object,
    modalSize: PropTypes.string,
});
const communicateTab = PropTypes.shape({
    id: PropTypes.number,
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    component: PropTypes.any.isRequired,
});
// changed to object due to Notifications module refactoring
const notification = PropTypes.object;
export const IMAGES = [
    'image/gif',
    'image/jpeg',
    'image/x-icon',
    'image/png',
    'image/bmp',
    'image/webp',
];
export const AUDIO = ['audio/*', 'audio/x-ms-wma', '.wma'];
export const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const PDF = 'application/pdf';
export const DOCUMENTS = [
    PDF,
    '.zip',
    'application/zip',
    'application/x-zip-compressed',
    'application/vnd.rar',
    'application/octet-stream',
    '.rar',
    'application/msword',
    'text/csv',
    'image/vnd.djvu',
    XLSX,
];
export const WORD = [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-word.document.macroEnabled.12',
    'application/vnd.ms-word.template.macroEnabled.12',
];
export const CSV = 'text/csv';
export const ALL_DOCUMENTS = [
    ...DOCUMENTS,
    '.pptx',
    '.ppt',
    '.wps',
    '.xml',
    '.xps',
    '.odp',
    'text/html',
    'text/xml',
    'application/vnd.ms-powerpoint',
    'text/plain',
    'application/rtf',
    'application/epub+zip',
    'application/vnd.ms-excel',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    XLSX,
    ...WORD,
];
export const VIDEOS = [
    'video/*',
    'application/x-shockwave-flash',
    'video/x-ms-wmv',
    'video/x-flv',
];
export const allowedFileTypes = [
    IMAGES,
    ...VIDEOS,
    ...AUDIO,
    ...DOCUMENTS,
    ...ALL_DOCUMENTS,
];
export default {
    fileUploadTab,
    fileAttachment,
    translatePropTypes,
    entityFormWrapTypes,
    entityFormPropTypes,
    featuredPermissionPropTypes,
    element,
    routerMatch,
    routerLocation,
    module,
    moduleGroup,
    modules,
    icon,
    type,
    selectValue,
    organisationType,
    organisation,
    status,
    statuses,
    statusesWithDraft,
    role,
    roleGroup,
    roleHierarchy,
    communicationHierarchy,
    routePropTypes,
    qualification,
    gender,
    timeFormats,
    timeZones,
    weekday,
    dateFormats,
    elementsOfType,
    personEntityType,
    formErrorMessage,
    socialAccountType,
    socialAccount,
    personPasswordInfo,
    passwordStrength,
    appConfig,
    sizeUnit,
    simpleTableConfig,
    simpleModalConfig,
    yearCalendar,
    communicateTab,
    notification,
    personEntityDefaultType,
    allowedFileTypes,
};
